import React, { useCallback, useEffect, useState } from 'react';
import '../merchants/components/CreateMerchant.css';
import './StorefrontUrls.css';
import '../transaction-monitoring/TransactionMonitoring.css';
import { BsPlusCircle } from 'react-icons/bs';
import Loader from '../../components/shared/Loader';
import StatusBadge from '../../components/shared/StatusBadge';
import {
  ConsoleActionButton,
  ListConsolePage,
} from '../../components/shared/ListConsoleLayout';
import { useAuthStore } from '../../store/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import { fetchMyStorefrontUrls } from './api/api';
import type { StorefrontUrlSubmissionRow } from './models/types';
import { formatSubmittedDateLabel, getApiErrorMessage } from './utils/utils';
import SubmitUrlDialog from './SubmitUrlDialog';

const StorefrontUrls: React.FC = () => {
  const authUser = useAuthStore((state) => state.user);
  const merchantId = authUser?.merchantId;
  const { canCreate } = usePermissions();
  const canSubmitUrl = canCreate('MERCH-STOREFRONT-URLS');

  const [rows, setRows] = useState<StorefrontUrlSubmissionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);

  const loadRows = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    fetchMyStorefrontUrls()
      .then(setRows)
      .catch((err: unknown) => setLoadError(getApiErrorMessage(err, 'Unable to load your URLs.')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (merchantId) loadRows();
  }, [merchantId, loadRows]);

  return (
    <ListConsolePage
      fill={false}
      title="URLs"
      // subtitle="Submit the domains your checkout runs on for approval"
      error={!loading && loadError ? loadError : null}
      actions={
        merchantId && canSubmitUrl ? (
          <ConsoleActionButton onClick={() => setShowSubmitDialog(true)}>
            <BsPlusCircle className="h-3 w-3 shrink-0" aria-hidden />
            Add URL
          </ConsoleActionButton>
        ) : undefined
      }
    >
      {!merchantId ? (
        <div className="cm-card console-body-card">
          <p className="cm-field-hint">
            URL approvals are managed per merchant account. Sign in as (or impersonate) a
            merchant to submit one.
          </p>
        </div>
      ) : (
        <div className="cm-card console-body-card" style={{ position: 'relative', minHeight: loading ? 160 : undefined }}>
          <div className="cm-step-body">
            <div className="cm-section-head">
              <h4 className="cm-section-title">Your Submissions</h4>
            </div>

            {loading ? (
              <Loader size="md" label="Loading your submissions…" />
            ) : rows.length === 0 ? (
              <p className="sfu-empty">You haven't submitted any URLs yet.</p>
            ) : (
              <table className="sfu-table">
                <thead>
                  <tr>
                    <th>URL</th>
                    <th>Status</th>
                    <th>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.url}</td>
                      <td>
                        <StatusBadge status={row.status} />
                        {row.status === 'REJECTED' && row.rejectionReason && (
                          <span className="sfu-reason">{row.rejectionReason}</span>
                        )}
                      </td>
                      <td>{formatSubmittedDateLabel(row.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <SubmitUrlDialog
        open={showSubmitDialog}
        onClose={() => setShowSubmitDialog(false)}
        onSubmitted={loadRows}
      />
    </ListConsolePage>
  );
};

export default StorefrontUrls;
